import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Modal,
  Button,
  TextField,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import {
  useLazyGetFamilyByIdQuery,
  useUpdateFamilyMutation,
} from "../services/sharedAccount";
import { showError } from "../constants/toasts";
import { SHARED_ACCOUNT_ROLE, SHARED_ACCOUNT_ROLE_NAME } from "../utils/enums";

interface EditRoleAccessModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  id: number | null;
}

export default function EditRoleAccessModal({
  open,
  onClose,
  setOpen,
  id,
}: EditRoleAccessModalProps) {
  const [userName, setUserName] = React.useState("");
  const [Relationship, setRelationship] = React.useState("1");
  const handleRelationshipChange = (event: SelectChangeEvent) => {
    setRelationship(event.target.value as string);
  };
  const [Role, setRole] = React.useState("1");
  const handleRoleChange = (event: SelectChangeEvent) => {
    setRole(event.target.value as string);
  };

  // Checkbox states for permissions
  const [permissions, setPermissions] = React.useState({
    sharedGoals: false,
    sharedBudgets: false,
    sharedCalendar: false,
  });

  const handlePermissionChange = (permission: keyof typeof permissions) => {
    setPermissions((prev) => ({
      ...prev,
      [permission]: !prev[permission],
    }));
  };
  const [getbyId] = useLazyGetFamilyByIdQuery();
  const [updateFamilyData] = useUpdateFamilyMutation();
  const [loading, setLoading] = useState<boolean>();

  const handleUpdate = async () => {
    try {
      console.log("handleUpdate called", { id, Role, permissions });
      setLoading(true);
      if (!id) {
        console.log("No ID provided");
        return;
      }

      const updateData = {
        assigned_role: parseInt(Role),
        shared_goal: permissions.sharedGoals,
        shared_budget: permissions.sharedBudgets,
        shared_calendar: permissions.sharedCalendar,
      };

      console.log("Sending update data:", updateData);

      const response = await updateFamilyData({
        id,
        body: updateData,
      }).unwrap();

      console.log("Update response:", response);

      if (response?.status === 200) {
        // Success - close modal or show success message
        setOpen(false);
        // You can add a success toast here if needed
      }
    } catch (error: any) {
      console.error("Update error:", error);
      showError(error?.message || error?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) return;

      const response = await getbyId({ id }).unwrap();
      if (response?.status === 200 && response?.data) {
        const data = response.data;

        // Set user name from shared_user
        if (data.shared_user?.first_name) {
          setUserName(data.shared_user.first_name);
        }

        // Set assigned role
        if (data.assigned_role) {
          setRole(data.assigned_role.toString());
        }

        // Set permissions from the actual API response
        setPermissions({
          sharedGoals: data.shared_goal || false,
          sharedBudgets: data.shared_budget || false,
          sharedCalendar: data.shared_calendar || false,
        });
      }
    } catch (error: any) {
      showError(error?.message || error?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getDataByIdList();
    }
  }, [id]);
  return (
    <Modal className="modal editRoleAccess_modal" open={open} onClose={onClose}>
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>Edit Shared Role & Access</h2>
            <div className="btn-close" onClick={() => setOpen(false)}>
              <CloseIcon />
            </div>
          </div>

          <form className="form" onSubmit={(e) => e.preventDefault()}>
            <div className="gap_p">
              <div className="control_group">
                <label>Shared Account Manager</label>
                <TextField
                  fullWidth
                  value={userName}
                  disabled
                  variant="outlined"
                  placeholder="User name will appear here"
                />
              </div>
              <div className="control_group w_100">
                <label>Assign Role</label>
                <Select fullWidth value={Role} onChange={handleRoleChange}>
                  <MenuItem disabled value="default">
                    Select
                  </MenuItem>
                  <MenuItem value={SHARED_ACCOUNT_ROLE.OWNER.toString()}>
                    {SHARED_ACCOUNT_ROLE_NAME[SHARED_ACCOUNT_ROLE.OWNER]}
                  </MenuItem>
                  {/* <MenuItem value={SHARED_ACCOUNT_ROLE.PARTNER.toString()}>
                    {SHARED_ACCOUNT_ROLE_NAME[SHARED_ACCOUNT_ROLE.PARTNER]}
                  </MenuItem> */}
                  <MenuItem value={SHARED_ACCOUNT_ROLE.CONTRIBUTOR.toString()}>
                    {SHARED_ACCOUNT_ROLE_NAME[SHARED_ACCOUNT_ROLE.CONTRIBUTOR]}
                  </MenuItem>
                  <MenuItem value={SHARED_ACCOUNT_ROLE.VIEWER.toString()}>
                    {SHARED_ACCOUNT_ROLE_NAME[SHARED_ACCOUNT_ROLE.VIEWER]}
                  </MenuItem>
                </Select>
              </div>
              <div className="control_group">
                <label>Access Permissions</label>
                <FormGroup className="list">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={permissions.sharedGoals}
                        onChange={() => handlePermissionChange("sharedGoals")}
                      />
                    }
                    label="Shared Goals"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={permissions.sharedBudgets}
                        onChange={() => handlePermissionChange("sharedBudgets")}
                      />
                    }
                    label="Shared Budgets"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={permissions.sharedCalendar}
                        onChange={() =>
                          handlePermissionChange("sharedCalendar")
                        }
                      />
                    }
                    label="Shared Calendar"
                  />
                </FormGroup>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="footer_btn_flex">
              <Button className="btn btn_grey" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                className="btn btn_primary"
                onClick={(e) => {
                  e.preventDefault();
                  console.log("Button clicked");
                  handleUpdate();
                }}
                disabled={loading}
                type="button"
              >
                {loading ? "Updating..." : "Update Role & Permissions"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
